import React, { ReactNode } from 'react';

import { tokenFormatter } from '@/lib/formatting';

import { TokenIssuerBadge } from '@/components/Badges/TokenIssuerBadge';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { Badge } from '@/ui/Badge';

import { formatShortAddress } from '@/utils/address';
import { ChainAddress } from '@/utils/models';

import { fontStyles } from '../../../../../styles/typography';
import { PreviewToken } from '../../TokenPreviewPopover';

export interface SearchComboBoxItemProps {
  /** Token contract address */
  address: ChainAddress;
  /** Token balance */
  balance?: string;
  /** Token balance in USD */
  balanceUsd?: string;
  /** Chain ID for this token */
  chainId: number;
  /**
   * Optional reason a token is non-selectable, rendered inline next to the
   * symbol. Populated by `TokenSelectModal` from the consumer's
   * `filterToken` result.
   */
  disabledReason?: ReactNode;
  /** ID of the token issuer returned by Token API. */
  issuer?: string;
  /** ID of the launchpad used to launch the token */
  launchpad?: string | null;
  /** Token logo URL */
  logoUrl: string | undefined;
  /** Token name */
  name: string;
  /** Token symbol */
  symbol: string;
  /**
   * Whether our token API has flagged this token as verified.
   */
  verified?: boolean;
  /**
   * Whether the token is traded on a leading centralized exchange or major DEX.
   * Returned from token search.
   */
  trusted?: boolean;
  /** Callback to set a token into the preview popover */
  setPreviewToken: (token: PreviewToken | null) => void;
}

/** Token item in token search combobox
 * @param props - Token item component props `SearchComboBoxItemProps`
 */
const SearchComboBoxItemComponent = ({
  setPreviewToken,
  ...token
}: SearchComboBoxItemProps): React.ReactElement => {
  const { formatFiat } = useFiatDisplay();

  return (
    <div
      className="items-center flex justify-between gap-3 px-3 w-full"
      onMouseEnter={() => setPreviewToken(token ?? null)}
    >
      <div className="flex items-center gap-4 min-w-0">
        <TokenAvatar
          address={token.address}
          chainId={token.chainId}
          launchpad={token.launchpad}
          size={32}
        />
        <div className="grid grid-flow-row flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: ['overflow-hidden', 'text-ellipsis', 'whitespace-nowrap', 'min-w-0'],
              })}
              title={token.name}
            >
              {token.name}
            </span>
            {token.issuer && <TokenIssuerBadge id={token.issuer} size="sm" className="shrink-0" />}
          </div>
          <div className="flex items-center gap-1 min-w-0">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: [
                  'inline',
                  'text-token-gray-800',
                  'overflow-hidden',
                  'text-ellipsis',
                  'whitespace-nowrap',
                  'min-w-0',
                ],
              })}
              title={token.symbol}
            >
              {token.symbol}
            </span>
            {token.disabledReason !== undefined && token.disabledReason !== null && (
              <div className="shrink-0">
                <Badge variant="neutral">{token.disabledReason}</Badge>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {token.balance ? (
          <div className="flex flex-col items-end">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: ['flex'],
              })}
            >
              {tokenFormatter.format(token.balance, { symbol: token.symbol })}
            </span>
            {token.balanceUsd && (
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['flex', 'text-token-gray-800'],
                })}
              >
                {formatFiat(token.balanceUsd)}
              </span>
            )}
          </div>
        ) : (
          <span
            className={fontStyles({
              variant: 'CAPTION',
              class: ['text-token-gray-700', 'ml-2'],
            })}
          >
            {formatShortAddress(token.address)}
          </span>
        )}
      </div>
    </div>
  );
};
export const SearchComboBoxItem = React.memo(SearchComboBoxItemComponent);
